import React, { useState } from 'react';
import { ExternalLink, Library, BookOpen, ShoppingBag, Sparkles, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import LegalDisclaimer from '../components/LegalDisclaimer';

const GUMROAD_STORE = 'https://safehousefinance.gumroad.com';

const CATEGORIES = [
  'Investing',
  'Trading Psychology',
  'Business',
  'Entrepreneurship',
  'Financial Literacy',
  'AI Investing',
  'Macroeconomics',
  'Options Education',
  'Wealth Building',
];

/**
 * Ebook Library landing — embeds the Gumroad storefront.
 *
 * If Gumroad blocks <iframe> embedding we surface a prominent "Open Library"
 * CTA after a short grace period. Either way the page works.
 */
const Ebooks = () => {
  const { isPro } = useAuth();
  const [iframeFailed, setIframeFailed] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-[#07070d] via-[#0a0a14] to-[#0d0d1f] text-white p-8 md:p-10 mb-8 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-50 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.35) 0%, transparent 70%)' }}
          />
          <div
            aria-hidden
            className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full opacity-40 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.35) 0%, transparent 70%)' }}
          />
          <div className="relative flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-white/10 border border-white/20 text-cyan-300 font-medium">
                  <Sparkles size={11} className="mr-1" />
                  Neon Alpha · Ebook Library
                </Badge>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-3 tracking-tight">
                Premium investing & business{' '}
                <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">
                  ebooks
                </span>
              </h1>
              <p className="text-white/60 text-sm md:text-base leading-relaxed">
                Educational guides across investing, trading psychology, AI strategy, macroeconomics, and wealth
                building. Powered by the Safehouse Finance Gumroad storefront — every purchase is a one-time
                educational license.
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full md:w-auto">
              <Button
                onClick={() => window.open(GUMROAD_STORE, '_blank', 'noopener')}
                className="bg-gradient-to-r from-cyan-400 to-violet-500 hover:opacity-90 text-black font-semibold w-full md:w-auto"
                data-testid="ebooks-open-library-btn"
              >
                <Library size={16} className="mr-1.5" />
                Open Library
                <ExternalLink size={13} className="ml-1.5" />
              </Button>
              <Button
                onClick={() => window.open(GUMROAD_STORE, '_blank', 'noopener')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 bg-white/5 w-full md:w-auto"
                data-testid="ebooks-store-home-btn"
              >
                <ShoppingBag size={14} className="mr-1.5" />
                Browse all products
              </Button>
            </div>
          </div>
        </div>

        {/* Categories pills */}
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-3">Categories</p>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <Badge
                key={c}
                variant="outline"
                className="border-gray-200 bg-white text-gray-700 hover:border-cyan-400 hover:text-cyan-600 cursor-pointer transition-colors px-3 py-1.5 text-xs"
              >
                <BookOpen size={11} className="mr-1.5" />
                {c}
              </Badge>
            ))}
          </div>
        </div>

        {/* Pro perks card */}
        {!isPro && (
          <Card className="mb-6 border-purple-200 bg-gradient-to-r from-purple-50 to-cyan-50" data-testid="ebooks-pro-promo">
            <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center">
                  <Lock className="text-white" size={18} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Premium ebooks are bundled with Pro</p>
                  <p className="text-xs text-gray-600">
                    Pro access unlocks the full reading library, member-only PDFs, and early-access guides.
                  </p>
                </div>
              </div>
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => (window.location.href = '/pro-access')}
                data-testid="ebooks-upgrade-btn"
              >
                Get Pro Access
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Embedded Gumroad storefront */}
        <Card className="overflow-hidden" data-testid="ebooks-embed-card">
          <CardContent className="p-0">
            {!iframeFailed ? (
              <div className="relative" style={{ minHeight: '900px' }}>
                <iframe
                  title="Safehouse Finance ebook library"
                  src={GUMROAD_STORE}
                  className="w-full"
                  style={{ minHeight: '900px', border: 'none' }}
                  loading="lazy"
                  onLoad={(e) => {
                    // If the storefront served an X-Frame-Options error page, the iframe
                    // will still fire onLoad but its body will be ~empty. We can't
                    // read the cross-origin doc, so we instead time-check below.
                    const iframe = e.target;
                    setTimeout(() => {
                      try {
                        // If contentDocument is null, the embed was blocked.
                        if (!iframe.contentWindow || iframe.offsetHeight < 100) {
                          setIframeFailed(true);
                        }
                      } catch (_) {
                        /* cross-origin: that's actually fine — means it loaded */
                      }
                    }, 4000);
                  }}
                  onError={() => setIframeFailed(true)}
                />
                {/* Header overlay note while the iframe is mounting */}
                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full px-3 py-1 text-[10px] text-gray-600 shadow-sm">
                  Embedded · powered by Gumroad
                </div>
              </div>
            ) : (
              <div className="text-center py-20 px-6" data-testid="ebooks-iframe-fallback">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-violet-500 mb-4">
                  <Library className="text-white" size={26} />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  The storefront blocks embedding.
                </h2>
                <p className="text-gray-600 mb-5 max-w-md mx-auto">
                  No problem — open the Safehouse Finance ebook library in a new tab to browse, preview, and
                  purchase any title.
                </p>
                <Button
                  onClick={() => window.open(GUMROAD_STORE, '_blank', 'noopener')}
                  className="bg-gradient-to-r from-cyan-400 to-violet-500 hover:opacity-90 text-black font-semibold"
                  data-testid="ebooks-fallback-open-btn"
                >
                  <Library size={16} className="mr-1.5" />
                  Open Ebook Library
                  <ExternalLink size={13} className="ml-1.5" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <LegalDisclaimer variant="membership" />
      </div>
    </div>
  );
};

export default Ebooks;
